// POS Systems catalog — single source of truth.
// Real product images live at /app/frontend/public/pos/{slug}.{ext}

export const POS_BRANDS = [
  {
    id: "clover",
    name: "Clover",
    description:
      "Clover offers flexible POS solutions for businesses that need modern checkout, payment acceptance, customer-facing tools, and business management features. From compact devices to full countertop systems, Clover helps businesses accept payments and manage day-to-day operations with a clean, professional setup.",
  },
  {
    id: "vst",
    name: "VST POS",
    description:
      "VST POS is a flexible point-of-sale option for businesses that need a customized checkout experience, simple transaction flow, and business tools that fit their operation.",
  },
  {
    id: "entreepos",
    name: "entreePOS",
    description:
      "entreePOS is designed for restaurants and food-service businesses that need a simple, easy-to-use POS system for orders, payments, pickup, delivery, dine-in, and restaurant workflow.",
  },
  {
    id: "rcc123",
    name: "RCC123 POS",
    description:
      "RCC123 POS is a retail-focused point-of-sale solution designed for businesses that need flexible checkout, secure transactions, and scalable tools for daily operations.",
  },
];

export const POS_SYSTEMS = [
  // ─── CLOVER ───────────────────────────────────────────────
  {
    slug: "clover-station-solo",
    name: "Clover Station Solo",
    brand: "clover",
    brandName: "Clover",
    imageExt: "png",
    description:
      "Clover Station Solo is a powerful countertop POS system built for businesses that need a complete front-counter checkout solution. It helps manage payments, orders, receipts, and day-to-day operations in one clean system.",
    bestFor:
      "Restaurants, retailers, and service businesses that want a clean countertop setup.",
    features: [
      "Large touchscreen display",
      "EMV, contactless, swipe",
      "Integrated printer and cash drawer support",
      "Order, payment, and receipt workflow",
      "Cloud-based reporting access",
    ],
  },
  {
    slug: "clover-station-duo",
    name: "Clover Station Duo",
    brand: "clover",
    brandName: "Clover",
    imageExt: "png",
    description:
      "Clover Station Duo gives businesses a full POS setup with a customer-facing display for a smoother checkout experience. It is a strong fit for high-volume counters, restaurants, retail stores, and service businesses.",
    bestFor:
      "Full-service restaurants, busy retail counters, and customer-forward checkout lanes.",
    features: [
      "Dual-screen merchant + customer display",
      "EMV, contactless, swipe",
      "Tip, signature, and receipt prompts",
      "Integrated printer and cash drawer support",
      "Full Clover app ecosystem",
    ],
  },
  {
    slug: "clover-mini",
    name: "Clover Mini",
    brand: "clover",
    brandName: "Clover",
    imageExt: "png",
    description:
      "Clover Mini is a compact POS system designed for businesses that want powerful checkout tools without taking up too much counter space. It supports fast payments, receipts, and daily business functions.",
    bestFor: "Small retail, salons, quick service, and pop-ups.",
    features: [
      "Color touchscreen",
      "EMV, contactless, swipe",
      "Built-in printer",
      "Apps and inventory tools",
      "Wi-Fi, Ethernet, optional 4G",
    ],
  },
  {
    slug: "clover-flex",
    name: "Clover Flex",
    brand: "clover",
    brandName: "Clover",
    imageExt: "png",
    description:
      "Clover Flex is a handheld POS device built for flexible payment acceptance. It works well for tableside payments, line-busting, mobile checkout, and businesses that need payments away from the counter.",
    bestFor:
      "Full-service restaurants, mobile businesses, and roaming retail staff.",
    features: [
      "Handheld portable design",
      "Wi-Fi and 4G connectivity",
      "EMV, contactless, swipe",
      "Built-in printer and barcode scanner",
      "Long battery life",
    ],
  },
  {
    slug: "clover-go",
    name: "Clover Go",
    brand: "clover",
    brandName: "Clover",
    imageExt: "png",
    description:
      "Clover Go is a simple mobile payment solution for businesses that need to accept payments on the go. It is a good option for mobile businesses, service providers, events, and quick card acceptance.",
    bestFor:
      "Mobile sellers, food trucks, contractors, and field service businesses.",
    features: [
      "Pairs via Bluetooth",
      "EMV chip, contactless, swipe",
      "Works with iOS and Android",
      "Take payments anywhere",
    ],
  },

  // ─── VST POS ──────────────────────────────────────────────
  {
    slug: "vst-pos",
    name: "VST POS",
    brand: "vst",
    brandName: "VST POS",
    imageExt: "png",
    description:
      "VST POS is a practical point-of-sale system built for businesses that need a reliable checkout solution with customizable pricing, transaction flow, and daily POS tools. It is designed to help businesses run smoother at the counter.",
    bestFor:
      "Retailers and service businesses that want a flexible, customizable POS workflow.",
    features: [
      "Customizable pricing and product setup",
      "Secure payment acceptance",
      "Practical day-to-day POS workflow",
      "Receipts and transaction history",
    ],
  },

  // ─── entreePOS ────────────────────────────────────────────
  {
    slug: "entreepos-main",
    name: "entreePOS Main System",
    brand: "entreepos",
    brandName: "entreePOS",
    imageExt: "png",
    description:
      "entreePOS is designed for restaurants and food-service businesses that need a simple system for dine-in, pickup, delivery, and order management. It helps staff move faster while keeping operations organized.",
    bestFor: "Restaurants, delis, and food-service businesses.",
    features: [
      "Dine-in, pickup, and delivery workflow",
      "Order management and modifiers",
      "Payment acceptance at the table or counter",
      "Easy staff training",
    ],
  },
  {
    slug: "entreepos-payment",
    name: "entreePOS Payment Screen",
    brand: "entreepos",
    brandName: "entreePOS",
    imageExt: "png",
    description:
      "The entreePOS payment screen is designed to make checkout simple and efficient. It gives staff a clear payment interface for cash, card, tips, discounts, and customer transactions.",
    bestFor: "Restaurant staff handling card, cash, and split payments.",
    features: [
      "Clear payment workflow",
      "Tip and split payment support",
      "Cash and card acceptance",
      "Fast, staff-friendly screens",
    ],
  },
  {
    slug: "entreepos-order",
    name: "entreePOS Order Screen",
    brand: "entreepos",
    brandName: "entreePOS",
    imageExt: "png",
    description:
      "The entreePOS order screen helps restaurants organize menu items, categories, and order flow. It is built for fast item selection and smoother service during busy hours.",
    bestFor: "Busy kitchens and front-of-house staff.",
    features: [
      "Quick menu navigation",
      "Modifier and combo support",
      "Order tracking and updates",
      "Designed for staff speed",
    ],
  },
  {
    slug: "entreepos-customer-entry",
    name: "entreePOS Customer/Order Entry",
    brand: "entreepos",
    brandName: "entreePOS",
    imageExt: "png",
    description:
      "The entreePOS customer and order entry screen is useful for pickup, delivery, and customer-based ordering. It helps businesses collect customer details and manage order information more clearly.",
    bestFor: "Restaurants with active pickup and delivery operations.",
    features: [
      "Customer record management",
      "Pickup and delivery order entry",
      "Address and contact storage",
      "Linked order history",
    ],
  },

  // ─── RCC123 POS ───────────────────────────────────────────
  {
    slug: "rcc123-pos",
    name: "RCC123 POS",
    brand: "rcc123",
    brandName: "RCC123 POS",
    imageExt: "webp",
    description:
      "RCC123 POS is a flexible POS solution built for retail and service businesses that need secure transactions, customizable checkout tools, and scalable business features. It is designed to support daily operations and payment acceptance.",
    bestFor:
      "Retail and service businesses that want a scalable POS workflow.",
    features: [
      "Customizable POS setup",
      "Secure payment acceptance",
      "Inventory and product management",
      "Receipts and transaction history",
    ],
  },
];
